import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { catchError, tap, throwError } from 'rxjs';
import { ErrorVTO, NewRecordVTO } from '../../pages/admin-dashboard/products/product.model';
import { AuthService } from '../auth-service';
import { SubcategoryResultSet } from '../../pages/admin-dashboard/categories/category.model';
import {
  OneSubcategoryModel,
  SubcategoriesList,
  SubcategoriesListRequest,
  SubcategoriesListResponse,
} from '../../pages/admin-dashboard/admin-subcategories/admin-subcategories.model';

@Injectable({ providedIn: 'root' })
export class SubcategoryService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private API = environment.productsApiUrl;

  getAllSubcategories(paramsIn?: {
    subcategoryTitle?: string;
    pageSize?: number;
    pageOffset?: number;
    pageNum?: number;
    orderBy?: 'CREATION_DATE' | 'CATEGORY_TITLE' | 'TITLE';
    orderDir?: 'ASC' | 'DESC';
  }) {
    let params = new HttpParams();
    if (paramsIn) {
      if (paramsIn.subcategoryTitle) {
        params = params.set('subcategoryTitle', paramsIn.subcategoryTitle);
      }
      if (paramsIn.pageSize) {
        params = params.set('pageSize', paramsIn.pageSize.toString());
      }
      if (paramsIn.pageOffset) {
        params = params.set('pageOffset', paramsIn.pageOffset.toString());
      }
      if (paramsIn.pageNum) {
        params = params.set('pageNum', paramsIn.pageNum.toString());
      }
      if (paramsIn.orderBy) {
        params = params.set('orderBy', paramsIn.orderBy);
      }
      if (paramsIn.orderDir) {
        params = params.set('orderDir', paramsIn.orderDir);
      }
    }
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.get<SubcategoriesListResponse>(`${this.API}/subcategories`, { params, headers }).pipe(
      tap(() => console.log('subcategories retrieved successfully')),
      catchError(this.handleError),
    );
  }

  createSubcategory(dto: SubcategoriesListRequest) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.post<NewRecordVTO>(`${this.API}/subcategories`, dto, { headers }).pipe(
      tap(() => console.log('Category created successfully')),
      catchError(this.handleError),
    );
  }

  getSubcategoryById(subcategoryId: number) {
    console.log('fetching subcategory with id:', subcategoryId);
    let headers = new HttpHeaders();
    // headers = headers.set('X-No-Cache', 'true');
    const token = this.authService.getAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.get<OneSubcategoryModel>(`${this.API}/subcategories/${subcategoryId}`, { headers }).pipe(
      tap(() => console.log('subcategory retrieved successfully')),
      catchError(this.handleError),
    );
  }

  updateSubcategory(subcategoryId: number, dto: SubcategoriesListRequest) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.put<void>(`${this.API}/subcategories/${subcategoryId}`, dto, { headers }).pipe(
      tap(() => console.log('subcategory updated successfully')),
      catchError(this.handleError),
    );
  }

  activateSubcategory(subcategoryId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.patch<void>(`${this.API}/subcategories/${subcategoryId}/activate`, {}, { headers }).pipe(
      tap(() => console.log('subcategory activated successfully')),
      catchError(this.handleError),
    );
  }

  deactivateSubcategory(subcategoryId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.patch<void>(`${this.API}/subcategories/${subcategoryId}/deactivate`, {}, { headers }).pipe(
      tap(() => console.log('subcategory deactivated successfully')),
      catchError(this.handleError),
    );
  }

  deleteSubcategory(subcategoryId: number) {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return this.http.delete<void>(`${this.API}/subcategories/${subcategoryId}`, { headers }).pipe(
      tap(() => console.log('subcategory deleted successfully')),
      catchError(this.handleError),
    );
  }

  getSubcategories(paramsIn: { subcategoryId: number; orderBy?: string; orderDir?: 'ASC' | 'DESC' }) {
    let params = new HttpParams()
      .set('subcategoryId', paramsIn.subcategoryId.toString())
      .set('orderBy', paramsIn.orderBy || 'CREATION_DATE');

    if (paramsIn.orderDir) {
      params = params.set('orderDir', paramsIn.orderDir);
    }

    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get<SubcategoryResultSet>(`${this.API}/subcategories`, { params, headers }).pipe(
      tap(() => console.log(`Subcategories for category ${paramsIn.subcategoryId} retrieved`)),
      catchError(this.handleError),
    );
  }

  private handleError(error: HttpErrorResponse) {
    const apiError: ErrorVTO = error.error;
    console.error('An API error occurred:', apiError);
    return throwError(() => apiError);
  }
}
